import { DEFAULT_AVATAR } from "@constants/global";
import Image from "next/image";
import { useEffect, useState } from "react";
import { getInitials } from "utils/getInitials";

interface AvatarWithInitialsProps {
  readonly size?: "xs" | "sm" | "md" | "lg";
  readonly customSize?: number | undefined | null;
  readonly urlImg: string | null | undefined;
  /**
   * Initials to show in the avatar if there is no image MAX 2 characters
   * @example
   * ```tsx
   * initials="React Component Example"
   * ```
   * @returns
   * ```tsx
   * RC
   * ```
   */
  readonly initials: string;
  readonly title?: string | React.ReactNode;
  readonly subtitle?: string | React.ReactNode;
}

// AvatarWithInitials component displays a user avatar image or, if not available, a fallback with user initials.
// It supports different sizes and custom sizes, and can show a title and subtitle next to the avatar.
export default function AvatarWithInitials(props: AvatarWithInitialsProps) {
  // styleSize holds the CSS size for the avatar (width/height in px)
  const [styleSize, setStyleSize] = useState("42px");
  // imgError tracks if the image failed to load, to show the fallback
  const [imgError, setImgError] = useState(false);
  const { urlImg, initials, size = "md", customSize } = props;
  let styleNumberSize = 42;

  useEffect(() => {
    // Set avatar size based on props (customSize or size)
    if (customSize) {
      styleNumberSize = customSize;
      setStyleSize(styleNumberSize + "px");
    }

    if (size === "xs" && !customSize) {
      styleNumberSize = 18;
      setStyleSize(styleNumberSize + "px");
    }
    if (size === "sm" && !customSize) {
      styleNumberSize = 24;
      setStyleSize(styleNumberSize + "px");
    }
    if (size === "md" && !customSize) {
      styleNumberSize = 42;
      setStyleSize(styleNumberSize + "px");
    }

    if (size === "lg") {
      styleNumberSize = 54;
      setStyleSize(styleNumberSize + "px");
    }
  }, [size, customSize]);

  useEffect(() => {
    setImgError(false);
  }, [urlImg]);

  // If neither initials nor image URL are provided, render nothing
  if (!initials && !urlImg) {
    return null;
  }

  // Use customSize if provided, otherwise default to 42px
  const fallbackSize = customSize ? `${customSize}px` : "42px";
  const imageSizes = customSize ? `${customSize}px` : styleSize;

  // Helper to validate if the urlImg is a valid image URL for next/image
  function isValidImageUrl(url: string | null | undefined) {
    if (!url || url === DEFAULT_AVATAR) return false;
    // Accepts absolute URLs or relative URLs starting with '/'
    return (
      url.startsWith("/") ||
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("data:")
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* If there is a valid image URL and no error, show the image. Otherwise, show the fallback with initials. */}
      {isValidImageUrl(urlImg) && !imgError ? (
        <>
          <picture
            className="relative grid"
            style={{
              minWidth: styleSize,
              minHeight: styleSize,
              maxWidth: styleSize,
              maxHeight: styleSize,
              borderRadius: "50%",
              overflow: "hidden"
              //border: "2px solid white",
            }}
          >
            {/* If the image is not the default avatar, render the image. Otherwise, render the fallback. */}
            {urlImg !== DEFAULT_AVATAR && urlImg ? (
              <Image
                src={urlImg}
                alt="avatar"
                fill
                sizes={imageSizes}
                style={{ objectFit: "cover" }}
                onError={() => setImgError(true)} // Set error state if image fails to load
              />
            ) : (
              // Fallback: show initials in a styled div
              <div
                className={
                  "relative flex items-center justify-center overflow-x-hidden bg-gray-200"
                }
                style={{
                  minHeight: fallbackSize,
                  minWidth: fallbackSize,
                  borderRadius: "50%"
                }}
              >
                <span className="text-xl font-bold text-gray-800">
                  {getInitials(initials)}
                </span>
              </div>
            )}
          </picture>
          {/* Optionally render title and subtitle if provided */}
          {props.title && (
            <div className="grid w-auto justify-start text-wrap">
              <div className="grid text-wrap text-left">{props.title}</div>
              {props.subtitle && (
                <p className="grid text-wrap text-xs text-gray-600 dark:text-gray-300">
                  {props.subtitle}
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="flex items-center gap-2">
          <div
            className="relative flex items-center justify-center overflow-x-hidden bg-gray-200"
            style={{
              minHeight: fallbackSize,
              minWidth: fallbackSize,
              borderRadius: "50%"
            }}
          >
            <span
              className="font-bold text-gray-800"
              style={{
                fontSize: `calc(${fallbackSize} / 2.5)`, // Adjust font size relative to the parent size
                fontWeight: 400,
                fontStyle: "normal"
              }}
            >
              {getInitials(initials)}
            </span>
          </div>

          {props.title && (
            <div className="grid w-auto justify-start text-wrap">
              <div className="grid text-wrap text-left">{props.title}</div>
              {props.subtitle && (
                <p className="grid text-wrap text-xs text-gray-600 dark:text-gray-300">
                  {props.subtitle}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
